import { Center, Loader } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { apiFetch } from "@/lib/api-client";
import { useServerLangGate } from "@/lib/i18n/useServerLang";
import type { MeResponse } from "@/lib/queries/useMe";
import { queryKeys } from "@/lib/query-keys";
import { useUserStore } from "@/store/user-store";

/**
 * 어드민 가드 — 시스템 관리자 콘솔(공급사/슈퍼관리자) 접근 제어.
 *
 * AuthGuard 와 동일하게 세션 복원(isHydrated)을 대기하되, 추가로 슈퍼관리자 여부를 확인함.
 * 미인증이면 /login, 인증됐으나 슈퍼관리자가 아니면 회사 앱(/workspaces)으로 리다이렉트함.
 *
 * 신뢰 경계는 서버. 이 가드는 UX 보조지만 **어드민 진입 시 서버 /auth/me 를 최신 재확인(staleTime 0)**
 * 하여 스토어/캐시가 stale 인 경우(예: 권한 강등 전 로그인, 서버 정책 변경)에도 즉시 차단함.
 * (재확인이 없으면 stale 한 superAdmin=true 로 어드민 셸이 렌더되고 어드민 API 가 403 을 반환해
 * 빈 화면·로그아웃 불가 상태가 될 수 있음.)
 */
interface AdminGuardProps {
  children: ReactNode;
}

export function AdminGuard({ children }: AdminGuardProps) {
  const { t } = useTranslation();
  const user = useUserStore((s) => s.user);
  const isHydrated = useUserStore((s) => s.isHydrated);
  const setUser = useUserStore((s) => s.setUser);
  const location = useLocation();

  // 어드민 진입 시 서버 권위(/auth/me)로 슈퍼관리자 여부를 최신 재확인.
  const me = useQuery({
    queryKey: queryKeys.me.current(),
    queryFn: () => apiFetch<MeResponse>("/api/auth/me"),
    enabled: Boolean(user),
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
  });

  // 어드민 콘솔도 UI 언어는 서버 저장값(users.locale)을 따르고, 적용 전에는 그리지 않는다(AuthGuard 와 동일 규칙).
  const langReady = useServerLangGate(!me.isPending, me.data?.locale);

  // 최신 응답의 superAdmin 을 스토어에 반영(회사 앱/사이드바 게이트 일관성). 렌더 중 setState 금지 → effect.
  const serverSuper = me.data?.superAdmin;
  useEffect(() => {
    if (typeof serverSuper === "boolean") {
      const cur = useUserStore.getState().user;
      if (cur && cur.isSuperAdmin !== serverSuper) {
        setUser({ ...cur, isSuperAdmin: serverSuper });
      }
    }
  }, [serverSuper, setUser]);

  if (!isHydrated) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  // 서버 /me 재확인 진행 중이면 대기(오통과로 어드민 API 403 → 빈 화면 방지). 서버 언어 적용 전에도 대기.
  if (me.isPending || !langReady) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  // 서버 권위: 최신 /me 우선, 조회 실패 시 스토어 폴백. 슈퍼관리자 아니면 회사 앱으로.
  const isSuper = me.data ? me.data.superAdmin === true : user.isSuperAdmin === true;
  if (!isSuper) {
    // 어드민 서브도메인(admin.*)에는 회사 앱 라우트(/workspaces 등)가 없음. 여기서 /workspaces 로
    // 리다이렉트하면 매칭 라우트가 없어 다시 이 가드로 들어와 **무한 루프·빈 화면**이 됨.
    // → 서브도메인에서는 회사 호스트로 안내(하드 링크) + 로그아웃을 제공하고, 그 외(같은 호스트의
    //   /admin/* 폴백)에서는 SPA 내부 /workspaces 로 리다이렉트함.
    const onAdminHost =
      typeof window !== "undefined" && window.location.hostname.startsWith("admin.");
    if (!onAdminHost) {
      return <Navigate to="/workspaces" replace />;
    }
    const companyUrl = `${window.location.protocol}//${window.location.host.replace(
      /^admin\./,
      "",
    )}/workspaces`;
    const doLogout = () => {
      try {
        window.localStorage.clear();
        window.sessionStorage.clear();
      } catch {
        /* ignore */
      }
      window.location.assign("/login");
    };
    return (
      <Center mih="100vh" style={{ padding: 24 }}>
        <div
          style={{
            maxWidth: 420,
            width: "100%",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "28px 26px",
            textAlign: "center",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔒</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
            {t("admin.headerTitle")}
          </div>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.6 }}>
            {t("admin.guard.noAccess", { name: user.name })}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <a
              href={companyUrl}
              style={{
                padding: "9px 18px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#374151",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {t("admin.guard.goCompany")}
            </a>
            <button
              type="button"
              onClick={doLogout}
              style={{
                padding: "9px 18px",
                borderRadius: 8,
                border: "none",
                background: "#4f46e5",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t("auth.logout")}
            </button>
          </div>
        </div>
      </Center>
    );
  }

  return <>{children}</>;
}
